/**
 * React Query query hooks for RFP data fetching
 */

import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useApi } from "@platform/api";

import { listRfpCoverages, RfpCoverageResponse } from "./client";

/**
 * Query keys for React Query cache management
 */
export const queryKeys = {
  rfpCoverages: (rfpId: string) => ["rfp-coverages", rfpId] as const,
};

/**
 * Hook to fetch all coverages for an RFP
 *
 * This provides the canonical source of truth for available coverages.
 * Use this to build coverage type → UUID mappings for benefit class management.
 *
 * @param rfpId - RFP ID to fetch coverages for
 * @param options - React Query options
 * @returns Query result with coverages data
 *
 * @example
 * ```tsx
 * function BenefitClassManager({ rfpId }: { rfpId: string }) {
 *   const { data: coverages = [] } = useRfpCoverages(rfpId);
 *
 *   // Build coverage type -> ID map
 *   const coverageMap = useMemo(() => {
 *     return new Map(coverages.map(c => [c.type, c.id]));
 *   }, [coverages]);
 *
 *   // Now can map types to IDs when building coverage assignments
 *   const dentalId = coverageMap.get("dental");
 * }
 * ```
 */
export const useRfpCoverages = (
  rfpId: string,
  options?: Omit<
    UseQueryOptions<RfpCoverageResponse[], Error>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<RfpCoverageResponse[], Error> => {
  const axiosInstance = useApi();

  return useQuery({
    queryKey: queryKeys.rfpCoverages(rfpId),
    queryFn: () => listRfpCoverages(rfpId, axiosInstance),
    enabled: !!rfpId,
    staleTime: 10 * 60 * 1000, // 10 minutes - coverage config doesn't change often
    ...options,
  });
};
